import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { v2Api } from './api'
import { Button } from './components/Field'
import { ErrorBanner, Spinner } from './components/Feedback'
import { Avatar } from './components/IssueBadges'
import { issueKeys } from './keys'
import { cn, relativeTime } from './lib/ui'
import { CommentBody, MentionTextarea } from './MentionTextarea'
import type { Comment, MemberRow } from './types'
import { useCommentMutations } from './useIssueMutations'

function authorName(comment: Comment): string {
  if (comment.author?.name) return comment.author.name
  if (comment.author_kind === 'agent') return 'Agent'
  if (comment.author_kind === 'system') return 'Taskly'
  return 'Someone'
}

function CommentRow({
  comment,
  members,
  canEdit,
  onEdit,
  onDelete,
  busy,
}: {
  comment: Comment
  members: MemberRow[]
  canEdit: boolean
  onEdit: (body: string) => void
  onDelete: () => void
  busy: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.body)

  return (
    <li className="flex gap-2.5">
      <Avatar user={comment.author ?? null} size="sm" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
            {authorName(comment)}
          </span>
          {comment.author_kind === 'agent' && (
            <span className="rounded bg-ink-100 px-1 text-[10px] font-medium uppercase tracking-wide text-ink-500 dark:bg-ink-800">
              agent
            </span>
          )}
          <span className="text-xs text-ink-500">{relativeTime(comment.created_at)}</span>
          {comment.edited_at && <span className="text-xs text-ink-400">· edited</span>}
        </div>

        {editing ? (
          <div className="mt-1.5 space-y-2">
            <MentionTextarea
              value={draft}
              onChange={setDraft}
              members={members}
              rows={3}
              onSubmit={() => {
                onEdit(draft)
                setEditing(false)
              }}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="primary"
                disabled={!draft.trim() || busy}
                onClick={() => {
                  onEdit(draft)
                  setEditing(false)
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft(comment.body)
                  setEditing(false)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5">
            <CommentBody body={comment.body} mentioned={comment.mentioned_users} />
            {canEdit && (
              <div className="mt-1 flex gap-3 text-xs text-ink-500">
                <button
                  type="button"
                  className="hover:text-ink-800 hover:underline dark:hover:text-ink-200"
                  onClick={() => {
                    setDraft(comment.body)
                    setEditing(true)
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="hover:text-red-600 hover:underline"
                  onClick={onDelete}
                  disabled={busy}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

/**
 * The comment thread.
 *
 * Worth knowing: a comment write never touches `tasks.updated_at` on the
 * server. The iPad fingerprints tasks as "id-updatedAtMs" and would re-merge
 * its entire board every time somebody commented, so the thread and the issue
 * row are deliberately independent — which is also why this component
 * invalidates the comment query rather than the board.
 */
export function CommentSection({
  slug,
  issueRef,
  members,
  currentUserId,
  canWrite,
}: {
  slug: string | null
  issueRef: string
  members: MemberRow[]
  currentUserId: number | null
  canWrite: boolean
}) {
  const [draft, setDraft] = useState('')
  const { create, update, remove } = useCommentMutations(slug, issueRef)

  const comments = useQuery({
    queryKey: issueKeys.comments(slug ?? '', issueRef),
    queryFn: () => v2Api.comments(slug as string, issueRef),
    enabled: Boolean(slug && issueRef),
    select: (response) => response.data,
  })

  const submit = () => {
    const body = draft.trim()
    if (!body) return
    create.mutate(body, { onSuccess: () => setDraft('') })
  }

  return (
    <section aria-label="Comments" className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
        Comments
        {comments.data && comments.data.length > 0 && (
          <span className="ml-1 font-normal tabular-nums">({comments.data.length})</span>
        )}
      </h3>

      {canWrite && (
        <div className="space-y-2">
          <MentionTextarea
            value={draft}
            onChange={setDraft}
            members={members}
            placeholder="Add a comment. Type @ to mention a teammate."
            onSubmit={submit}
          />
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={submit} disabled={!draft.trim() || create.isPending}>
              {create.isPending ? 'Posting…' : 'Comment'}
            </Button>
            <span className="text-xs text-ink-400">⌘↵ to post</span>
          </div>
          {create.error && <ErrorBanner error={create.error} />}
        </div>
      )}

      {comments.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-ink-500">
          <Spinner /> Loading comments…
        </div>
      ) : comments.error ? (
        <ErrorBanner error={comments.error} onRetry={() => void comments.refetch()} />
      ) : comments.data && comments.data.length > 0 ? (
        <ul className={cn('space-y-4')}>
          {comments.data.map((comment) => (
            <CommentRow
              key={comment.id}
              comment={comment}
              members={members}
              canEdit={canWrite && comment.author_id === currentUserId}
              busy={update.isPending || remove.isPending}
              onEdit={(body) => {
                const trimmed = body.trim()
                if (trimmed && trimmed !== comment.body) {
                  update.mutate({ id: comment.id, body: trimmed })
                }
              }}
              onDelete={() => remove.mutate(comment.id)}
            />
          ))}
        </ul>
      ) : (
        <p className="py-2 text-sm text-ink-500">No comments yet.</p>
      )}

      {(update.error || remove.error) && <ErrorBanner error={update.error ?? remove.error} />}
    </section>
  )
}
